/** Minimal starter copy — shows block layout without overwhelming delete work. */

import { freshJourneyTimeline } from '@/lib/notebook/models/journey-timeline-model'

export const HERO_BLOCK_DEFAULTS = Object.freeze({
  eyebrow: 'Notebook · Draft',
  title: 'Your notebook title',
  subtitle: 'A short line about what this page is for.',
})

export const CORE_CARD_BLOCK_DEFAULTS = Object.freeze({
  title: 'Core idea',
  badge: { text: 'focus', variant: 'purple' },
  mantra: '"One clear sentence that sets the tone."',
  rules: [
    { text: 'Lead with intent. State what matters in plain language.' },
    { text: 'Keep each line short so editing stays easy.' },
  ],
})

/** @deprecated Use CORE_CARD_BLOCK_DEFAULTS — kept for imports */
export const CARD_BLOCK_DEFAULTS = CORE_CARD_BLOCK_DEFAULTS

export const SUPPORTING_NOTES_BLOCK_DEFAULTS = Object.freeze({
  title: 'Supporting notes',
  badge: { text: 'optional', variant: 'blue' },
  rules: [
    { text: 'Add detail only when needed. Link out or expand below.' },
    { text: 'Add more points with the block plus control.' },
  ],
})

export const RULE_BLOCK_DEFAULTS = Object.freeze({
  title: 'Rules',
  rules: [
    { text: 'Start simple. One rule per line keeps scanning easy.' },
    { text: 'Replace this text with your own checklist or constraints.' },
  ],
})

export const QUOTE_BLOCK_DEFAULTS = Object.freeze({
  text: '"A single quote or principle you want visible."',
})

/** @deprecated Use QUOTE_BLOCK_DEFAULTS */
export const MANTRA_BLOCK_DEFAULTS = QUOTE_BLOCK_DEFAULTS

export const BADGE_BLOCK_DEFAULTS = Object.freeze({
  badges: [
    { text: 'draft', variant: 'purple' },
    { text: 'review', variant: 'amber' },
    { text: 'done', variant: 'green' },
  ],
})

export const ZONE_GRID_DEFAULTS = Object.freeze({
  title: 'The three code zones — know exactly where you are',
  zones: [
    {
      id: 'you',
      className: 'zone-you',
      title: 'You own',
      items: ['Core logic', 'Data model'],
    },
    {
      id: 'together',
      className: 'zone-together',
      title: 'Together',
      items: ['Integrations', 'Edge cases'],
    },
    {
      id: 'cursor',
      className: 'zone-cursor',
      title: 'Assist',
      items: ['UI polish', 'Boilerplate'],
    },
  ],
})

export const DAILY_ROW_DEFAULTS = Object.freeze({
  title: 'Daily schedule',
  slots: [
    { label: 'Morning', title: 'Plan', detail: 'What you will focus on today.' },
    { label: 'Midday', title: 'Build', detail: 'Execute the main task block.' },
    { label: 'Evening', title: 'Review', detail: 'Note what shipped and what is next.' },
  ],
})

export const STEP_ROW_DEFAULTS = Object.freeze({
  title: 'Steps',
  steps: [
    {
      num: '1',
      title: 'Clarify the outcome',
      detail: 'Write one sentence for what done looks like.',
    },
    {
      num: '2',
      title: 'Take the next action',
      detail: 'Smallest step that moves it forward.',
    },
  ],
})

export const STEPS_BLOCK_DEFAULTS = Object.freeze({
  title: 'Workflow',
  steps: [
    {
      id: 'step-1',
      title: 'First step',
      detail: 'Short description of what happens here.',
      done: true,
    },
    {
      id: 'step-2',
      title: 'Next step',
      detail: 'Add or reorder steps as you go.',
      done: false,
    },
  ],
})

export const CALLOUT_BLOCK_DEFAULTS = Object.freeze({
  icon: 'idea',
  color: 'purple',
  content: 'A quick callout — tip, warning, or reminder.',
})

export const WARNING_BOX_DEFAULTS = Object.freeze({
  content: 'Important note: read this before continuing.',
})

export const INTERVIEWER_TEST_DEFAULTS = Object.freeze({
  title: 'The one-sentence rule',
  badge: { text: 'daily check', variant: 'green' },
  lead: 'The one-sentence rule:',
  body:
    'At the end of every session, write one sentence in plain English about what you built today. If you cannot write it, you did not understand it yet.',
})

export const CODE_BLOCK_DEFAULTS = Object.freeze({
  code: "const greet = (name) => `Hello, ${name}`\nconsole.log(greet('Inora'))",
  language: 'javascript',
  filename: 'example.ts',
})

export const MEDIA_BLOCK_DEFAULTS = Object.freeze({
  intro: '',
  items: [],
})

export const RICHTEXT_BLOCK_DEFAULTS = Object.freeze({
  blocks: [
    { id: 'rt-1', type: 'h3', content: 'Section heading' },
    {
      id: 'rt-2',
      type: 'p',
      content: 'Body text lives here — click to edit, or open the row menu to transform, insert blocks, or delete.',
    },
    {
      id: 'rt-3',
      type: 'blockquote',
      content: 'Pull quotes render in purple italic — use them for emphasis or citations.',
    },
  ],
})

export const INSIGHT_BLOCK_DEFAULTS = Object.freeze({
  chartType: null,
  title: '',
  insight: '',
  rows: [
    { id: 'd1', label: 'Mon', value: 4000 },
    { id: 'd2', label: 'Tue', value: 3000 },
    { id: 'd3', label: 'Wed', value: 5500 },
    { id: 'd4', label: 'Thu', value: 4280 },
    { id: 'd5', label: 'Fri', value: 6900 },
  ],
})

export const SUMMARY_BLOCK_DEFAULTS = Object.freeze({
  summaries: [
    {
      id: 'summary-1',
      title: 'Summary title',
      summary: 'Two or three sentences that capture the main point.',
      keyPoints: ['First key point', 'Second key point'],
      conclusion: 'Optional closing line.',
    },
  ],
})

export const QUESTION_BLOCK_DEFAULTS = Object.freeze({
  questions: [
    {
      id: 'q-1',
      question: 'What is the open question you are exploring?',
      thoughts: 'Sketch what you know and what you still need to learn.',
      linkedAnswer: 'Your working answer or hypothesis.',
      resolved: false,
    },
    {
      id: 'q-2',
      question: 'What did you decide already?',
      thoughts: '',
      linkedAnswer: null,
      resolved: true,
    },
  ],
})

export const REFERENCE_BLOCK_DEFAULTS = Object.freeze({
  references: [],
})

export const JOURNEY_TIMELINE_DEFAULTS = Object.freeze(
  JSON.parse(JSON.stringify(freshJourneyTimeline(7))),
)

/** @deprecated Use JOURNEY_TIMELINE_DEFAULTS */
export const TIMELINE_BLOCK_DEFAULTS = JOURNEY_TIMELINE_DEFAULTS
